import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { updateBookData, clearError } from '../../../store/slices/bookSlice';
import { useBookAPI } from '../../../hooks/useBookAPI';
import { trackBookFormFactorLanded, trackBookFormFactorClicked, trackBookType } from '../../../utils/analytics';
import { BOOK_SIZE_OPTIONS, PAPER_TYPE, BINDING_TYPE, MIN_PAGES } from '../../../constants/bookFormFactor';
import { useAlert } from '../../../components/AlertBanner';
import { Info } from 'lucide-react';

const BookFormFactorStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const bookData = useAppSelector((state) => state.book.bookData);

  const { saveBook, saveAndContinue, isSaving, error } = useBookAPI();

  useEffect(() => {
    trackBookFormFactorLanded('Book Form Factor');
  }, []);

  const handleSaveToDrafts = async () => {
    const result = await saveBook();
    if (result.meta.requestStatus === 'fulfilled') {
      showAlert('Saved to drafts successfully!', 'success');
    }
  };

  const handleSaveAndContinue = async () => {
    if (!bookData.bookSize) {
      showAlert('Please select a Book Size to continue', 'warning');
      return;
    }
    if (!bookData.bindingType) {
      showAlert('Please select a Binding Type to continue', 'warning');
      return;
    }
    if (!bookData.paperType) {
      showAlert('Please select a Paper Type to continue', 'warning');
      return;
    }

    trackBookType({
      trim_size: bookData.bookSize || '',
      binding: bookData.bindingType || '',
      print_color: bookData.interiorColor || '',
      paper: bookData.paperType || '',
      cover_finish: bookData.coverLamination || '',
    });
    await saveAndContinue('book-formatting');
  };

  return (
    <div className="bg-white md:bg-transparent min-h-screen md:min-h-0">
      {/* Desktop: Back Button */}
      <div className="hidden md:block mb-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
        >
          <span>←</span>
          <span>Go Back to Dashboard</span>
        </button>
      </div>

      {/* Mobile: Breadcrumb */}
      <div className="md:hidden bg-gray-50 px-4 py-3 border-b border-gray-200">
        <p className="text-xs text-gray-600">
          Book Info › Book Form Factor › Book Formatting
        </p>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 mx-4 md:mx-0">
          <div className="flex justify-between items-start">
            <p className="text-red-700 text-sm">{error}</p>
            <button
              onClick={() => dispatch(clearError())}
              className="text-red-600 hover:text-red-800 text-sm underline ml-4"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="bg-white md:bg-white p-4 md:p-8 rounded-none md:rounded-lg md:border md:border-gray-200 md:shadow-sm">
        <div className="space-y-5">
          {/* Booksize Selection */}
          <div>
            <label className="block text-gray-900 text-sm font-medium mb-2">
              Book Size
            </label>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
              {BOOK_SIZE_OPTIONS.map((size) => (
                <button
                  key={size.value}
                  onClick={() => { dispatch(updateBookData({ bookSize: size.value })); trackBookFormFactorClicked(size.label); }}
                  className={`flex flex-col items-center px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    bookData.bookSize === size.value
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  <span>{size.label}</span>
                  <span className={`text-[10px] font-normal ${bookData.bookSize === size.value ? 'text-orange-100' : 'text-gray-500'}`}>
                    {size.description}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Binding Type */}
          <div>
            <label className="block text-gray-900 text-sm font-medium mb-2">
              Binding Type
            </label>
            <select
              value={bookData.bindingType || 'Select type'}
              onChange={(e) => dispatch(updateBookData({ bindingType: e.target.value }))}
              className="w-full bg-gray-50 text-gray-900 px-3 py-2.5 rounded-md border border-gray-300 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:bg-white"
            >
              <option disabled>Select type</option>
              <option>{BINDING_TYPE.PAPERBACK}</option>
              <option>{BINDING_TYPE.HARDBACK}</option>
            </select>
          </div>

          {/* Book Interior Print Colour */}
          <div>
            <label className="block text-gray-900 text-sm font-medium mb-2">
              Book Interior Print Colour
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { value: 'Black & White Interior', sub: 'Text-only · Lower print cost' },
                { value: 'Coloured Interior',       sub: 'Images & illustrations · Art Matte recommended' },
              ].map(({ value, sub }) => {
                const active = bookData.interiorColor === value;
                return (
                  <button
                    key={value}
                    onClick={() => dispatch(updateBookData({ interiorColor: value }))}
                    className={`flex flex-col items-start px-4 py-2.5 rounded-md text-sm font-medium transition-colors text-left ${
                      active ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                    }`}
                  >
                    {value}
                    <span className={`text-[10px] font-normal mt-0.5 ${active ? 'text-orange-100' : 'text-gray-400'}`}>{sub}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Paper Type */}
          <div>
            <label className="block text-gray-900 text-sm font-medium mb-2">
              Paper Type
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { value: PAPER_TYPE.CREAM,     sub: 'Warm tone · Novels & poetry' },
                { value: PAPER_TYPE.WHITE,     sub: 'Bright white · Text-heavy books' },
                { value: PAPER_TYPE.ART_MATTE, sub: 'Premium · Photo & illustrated books' },
              ].map(({ value, sub }) => {
                const active = bookData.paperType === value;
                return (
                  <button
                    key={value}
                    onClick={() => dispatch(updateBookData({ paperType: value }))}
                    className={`flex flex-col items-start px-4 py-2.5 rounded-md text-sm font-medium transition-colors text-left ${
                      active ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                    }`}
                  >
                    {value}
                    <span className={`text-[10px] font-normal mt-0.5 ${active ? 'text-orange-100' : 'text-gray-400'}`}>{sub}</span>
                  </button>
                );
              })}
            </div>
            {bookData.bindingType && bookData.paperType && (() => {
              const min = MIN_PAGES[bookData.bindingType]?.[bookData.paperType as keyof (typeof MIN_PAGES)[string]];
              if (!min) return null;
              return (
                <div className="flex items-start gap-2 mt-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-md">
                  <Info className="text-blue-500 shrink-0 mt-0.5" size={14} />
                  <p className="text-blue-700 text-xs leading-relaxed">
                    Your book must contain <span className="font-semibold">more than {min} pages</span> to submit for {bookData.bindingType}.
                  </p>
                </div>
              );
            })()}
          </div>

          {/* Cover Lamination */}
          <div>
            <label className="block text-gray-900 text-sm font-medium mb-2">
              Cover Lamination
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={() => dispatch(updateBookData({ coverLamination: 'Gloss Finish' }))}
                className={`px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  bookData.coverLamination === 'Gloss Finish'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
              >
                Gloss Finish
              </button>
              <button
                onClick={() => dispatch(updateBookData({ coverLamination: 'Matte Finish' }))}
                className={`px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  bookData.coverLamination === 'Matte Finish'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                }`}
              >
                Matte Finish
              </button>
            </div>
            <p className="text-gray-500 text-xs mt-2">
              Usually used for books with Vibrant covers
            </p>
          </div>

          {/* Mobile Action Buttons */}
          <div className="md:hidden flex flex-col items-center space-y-3 pt-4">
            <button
              onClick={handleSaveToDrafts}
              disabled={isSaving}
              className="bg-white text-orange-500 px-8 py-3 rounded-md border-2 border-orange-500 hover:bg-orange-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleSaveAndContinue}
              disabled={isSaving}
              className="bg-gray-900 text-white px-8 py-3 rounded-md hover:bg-gray-800 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Continue'}
            </button>
          </div>

          {/* Desktop Action Buttons */}
          <div className="hidden md:flex gap-3 pt-4 justify-start">
            <button
              onClick={handleSaveToDrafts}
              disabled={isSaving}
              className="bg-transparent text-orange-500 px-8 py-3 rounded-lg border-2 border-orange-500 hover:bg-orange-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleSaveAndContinue}
              disabled={isSaving}
              className="bg-gray-900 text-white px-8 py-3 rounded-lg hover:bg-gray-800 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BookFormFactorStep;
